import _ from 'lodash';
import { useMemo } from 'react';
import { DatabaseEvent } from '@/types/event';
import { useQueryFiltersStore, useSourcesStore } from '@/stores';
import { splitSourceSelection } from '@/stores/sources.store';
import { SortOption, getDateRangeFromFilter } from '@/utils/query-filters';
import { toUtcTimestamp } from '@/utils/date';
import { useVideoSourceSelected } from '@/hooks';

type EventsDataMode = 'alerts' | 'events';

const SORT_OPTION_TO_ORDER_BY: Record<
    SortOption,
    Record<EventsDataMode, string>
> = {
    [SortOption.DATE_NEWEST]: {
        alerts: 'eventCreatedAt desc',
        events: 'firstFrameTime desc',
    },
    [SortOption.DATE_OLDEST]: {
        alerts: 'eventCreatedAt asc',
        events: 'firstFrameTime asc',
    },
    [SortOption.VIDEO_TIME_NEWEST]: {
        alerts: 'eventCreatedAt desc',
        events: 'firstFrameTime desc',
    },
    [SortOption.VIDEO_TIME_OLDEST]: {
        alerts: 'eventCreatedAt asc',
        events: 'firstFrameTime asc',
    },
    [SortOption.CONFIDENCE]: {
        alerts: 'confidence desc',
        events: 'createdAt desc',
    },
    [SortOption.SOURCE_A_Z]: {
        alerts: 'sourceName asc',
        events: 'sourceName asc',
    },
    [SortOption.SOURCE_Z_A]: {
        alerts: 'sourceName desc',
        events: 'sourceName desc',
    },
};

const DEFAULT_SORT_OPTION = SortOption.DATE_NEWEST;

export function getDefaultOrderBy(alerts: boolean): string {
    const mode: EventsDataMode = alerts ? 'alerts' : 'events';
    return SORT_OPTION_TO_ORDER_BY[DEFAULT_SORT_OPTION][mode];
}

export function sortOptionToOrderBy(
    sortOption: SortOption,
    alerts: boolean,
): string {
    const mode: EventsDataMode = alerts ? 'alerts' : 'events';
    const mapping = SORT_OPTION_TO_ORDER_BY[sortOption];
    if (mapping) {
        return mapping[mode];
    }
    return getDefaultOrderBy(alerts);
}

export const EVENTS_PAGE_SIZE = 12;

export function resolveListFetchOrderBy(
    filterParams: { sourceId?: string[]; folderId?: string[] },
    orderBy: string,
): {
    hasSourceFilter: boolean;
    hasFolderFilter: boolean;
    effectiveOrderBy: string;
} {
    const hasSourceFilter = Boolean(
        filterParams.sourceId && filterParams.sourceId.length > 0,
    );
    const hasFolderFilter = Boolean(
        filterParams.folderId && filterParams.folderId.length > 0,
    );
    const effectiveOrderBy =
        (hasSourceFilter || hasFolderFilter) && orderBy === 'confidence desc'
            ? 'createdAt desc'
            : orderBy;
    return { hasSourceFilter, hasFolderFilter, effectiveOrderBy };
}

export interface EventsDataResult<T> {
    events: T[];
    totalItems: number;
    pageStateKey?: string;
    error: Error | undefined;
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    loadMore: () => void;
    mutate: () => void;
}

export interface PaginatedEventsResult<T> {
    events: T[];
    totalItems: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    pageStateKey?: string;
    error: Error | undefined;
    isLoading: boolean;
    isFetching: boolean;
    mutate: () => void;
}

export interface DatabaseEventsFilterParams {
    sourceId?: string[];
    folderId?: string[];
    startTime?: number;
    endTime?: number;
}

const DATABASE_EVENTS_SORT_TO_ORDER_BY: Partial<Record<SortOption, string>> = {
    [SortOption.DATE_NEWEST]: 'createdAt desc',
    [SortOption.DATE_OLDEST]: 'createdAt asc',
    [SortOption.SOURCE_A_Z]: 'sourceName asc',
    [SortOption.SOURCE_Z_A]: 'sourceName desc',
};

export function databaseEventsSortToOrderBy(sortOption: SortOption): string {
    return DATABASE_EVENTS_SORT_TO_ORDER_BY[sortOption] ?? 'createdAt desc';
}

export function mergeLiveDatabaseEvents(
    fetched: DatabaseEvent[],
    liveEvents: DatabaseEvent[],
    { sourceId, startTime, endTime }: DatabaseEventsFilterParams,
): DatabaseEvent[] {
    const matchingLive = liveEvents.filter((databaseEvent) => {
        if (
            sourceId &&
            sourceId.length > 0 &&
            !sourceId.includes(databaseEvent.event.sourceId)
        ) {
            return false;
        }
        const createdAt = databaseEvent.createdAt ?? 0;
        if (startTime != null && createdAt < startTime) return false;
        if (endTime != null && createdAt > endTime) return false;
        return true;
    });

    return _.uniqBy(
        [...matchingLive, ...fetched].sort(
            (first, second) => (second.createdAt ?? 0) - (first.createdAt ?? 0),
        ),
        (item) => item.id,
    );
}

export interface BaseEventFilterParams {
    sourceId?: string[];
    folderId?: string[];
    startTime?: number;
    endTime?: number;
}

export function useBaseEventFilters(sourceId?: string): BaseEventFilterParams {
    const dateTime = useQueryFiltersStore((state) => state.filters.dateTime);
    const selected = useSourcesStore((state) => state.selected);
    const selectedFolders = useSourcesStore((state) => state.selectedFolders);
    const scopedSelection = useMemo(
        () => (sourceId ? new Set([sourceId]) : selected),
        [sourceId, selected],
    );
    const videoSourceSelected = useVideoSourceSelected(scopedSelection);

    const dateRange = useMemo(
        () => (videoSourceSelected ? null : getDateRangeFromFilter(dateTime)),
        [dateTime, videoSourceSelected],
    );

    return useMemo(() => {
        const params: BaseEventFilterParams = {};

        if (sourceId) {
            params.sourceId = [sourceId];
        } else if (selected.size > 0 || selectedFolders.size > 0) {
            const { folderIds, looseSourceIds } = splitSourceSelection(
                selected,
                selectedFolders,
            );
            if (folderIds.length > 0) {
                params.folderId = folderIds;
            }
            if (looseSourceIds.length > 0) {
                params.sourceId = looseSourceIds;
            }
        }

        if (dateRange) {
            params.startTime = toUtcTimestamp(dateRange.startDate);
            params.endTime = toUtcTimestamp(dateRange.endDate);
        }

        return params;
    }, [sourceId, selected, selectedFolders, dateRange]);
}

export function useSelectedSourceIds(sourceId?: string): string[] | undefined {
    const selected = useSourcesStore((state) => state.selected);

    return useMemo(() => {
        if (sourceId) return [sourceId];
        return selected.size > 0 ? Array.from(selected) : undefined;
    }, [sourceId, selected]);
}
